import React from 'react';
import { Link } from 'react-router-dom';
import { Link as LinkIcon, CheckCircle, Beaker } from 'lucide-react';
import { FracasIncident } from '../../types/fracas.types';
import { LabSample } from '../../contexts/LabSampleContext';

interface FracasLinkedRecordsProps {
    incident: FracasIncident;
    linkedLabSamples: LabSample[];
    onRequestLab: () => void;
}

export const FracasLinkedRecords: React.FC<FracasLinkedRecordsProps> = ({
    incident,
    linkedLabSamples,
    onRequestLab
}) => {
    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                <LinkIcon className="w-5 h-5" />
                Linked Records
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
                {/* RCA Link */}
                {incident.rootCauses && incident.rootCauses.length > 0 ? (
                    <div className="p-3 bg-purple-50 rounded-lg border border-purple-200 flex items-center justify-between">
                        <div>
                            <p className="font-medium text-purple-900 text-sm">Root Causes Identified</p>
                            <p className="text-xs text-purple-700">{incident.rootCauses.length} causes logged</p>
                        </div>
                    </div>
                ) : (
                    <Link to="/rca/5why" className="p-3 bg-gray-50 rounded-lg border border-gray-200 text-center hover:bg-gray-100 block">
                        <p className="text-gray-600 text-sm">+ Create RCA</p>
                    </Link>
                )}

                {/* 8D Link */}
                {incident.eightDId ? (
                    <div className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                        <div>
                            <p className="font-medium text-green-900 text-sm">8D Report Linked</p>
                            <Link to={`/8d/${incident.eightDId}`} className="text-xs text-green-700 hover:underline">View Report</Link>
                        </div>
                        <CheckCircle className="w-4 h-4 text-green-600" />
                    </div>
                ) : (
                    <Link to="/8d/create" className="p-3 bg-gray-50 rounded-lg border border-gray-200 text-center hover:bg-gray-100 block">
                        <p className="text-gray-600 text-sm">+ Start 8D Report</p>
                    </Link>
                )}

                {/* FMEA Link */}
                <div className="flex items-center justify-between p-3 bg-blue-50/50 rounded-lg border border-blue-200">
                    <div>
                        <p className="font-medium text-slate-900 text-sm">FMEA / Risk Link</p>
                        <p className="text-[10px] text-slate-500">Cross-reference with Design/Process Risks</p>
                    </div>
                    <Link
                        to="/analytics/fta"
                        className="px-2 py-1 bg-white border border-blue-200 rounded text-[10px] font-bold text-blue-600 hover:bg-blue-50 transition-colors"
                    >
                        CHECK RISKS
                    </Link>
                </div>
            </div>

            {/* Lab Samples List */}
            <div className="mt-4 space-y-2">
                <h4 className="text-sm font-semibold text-gray-700">Lab Reports</h4>
                {linkedLabSamples.length > 0 ? (
                    linkedLabSamples.map((sample) => (
                        <div key={sample.id} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                            <div className="flex items-center gap-3">
                                <Beaker className="w-4 h-4 text-blue-600" />
                                <div>
                                    <p className="font-medium text-gray-900 text-sm">Sample: {sample.sampleNumber}</p>
                                    <span className="text-xs text-gray-600">{sample.status.toUpperCase()}</span>
                                </div>
                            </div>
                            <Link to={`/lab/${sample.id}`} className="text-blue-600 hover:text-blue-800 text-xs font-medium">View →</Link>
                        </div>
                    ))
                ) : (
                    <button
                        onClick={onRequestLab}
                        className="w-full py-2 text-sm text-blue-600 border border-dashed border-blue-300 rounded-lg hover:bg-blue-50"
                    >
                        + Request Lab Analysis
                    </button>
                )}
            </div>
        </div>
    );
};
